import * as React from "react";
import * as Redux from "redux";
import * as S from "@styled";

import { connect } from "react-redux";

import * as validator from "validator";

import { Route, RouteComponentProps, Switch } from "react-router-dom";
import { ThunkDispatch } from "redux-thunk";
import { ThemeProvider } from "styled-components";

import { DispatchType, Utilities } from "../shared/utilities";
import { default as DiffWorkingProcess } from "./components/DiffWorkingProcess";
import { default as ProcessEditorContainer } from "./components/ProcessEditor";
import { default as RunOnceDialogWrapper } from "./components/RunOnceDialogWrapper";
import { PageLoadSpinner } from "shared/components/PageLoadSpinner";
import useUserTheme from "shared/styles/hooks/useUserTheme";

import { fetchProcess, receiveProcessError } from "./actions/FetchProcess";
import { fetchData, receiveAll } from "./actions/InitialFetchActions";
import { newProcess } from "./actions/NewProcess";
import { processFromTemplate } from "./actions/ProcessFromTemplate";
import { State } from "./model/State";
import { ParsedPathParts, ProcessType } from "./model/AppPath";
import { IElement, IProcessData } from "./model/IProcessData";
import { useBeforeunload } from "shared/components/WarnAboutUnsavedChanges";
import { loadFromLocalStorage, removeFromLocalStorage } from "./actions/ProcessToLocalStorageActions";
import { DependencyConstraintsConfirmationModal } from "./components/Modals/DependencyConstraintsConfirmationModal";
import { ProcessDocumentationViewWrapper } from "./components/Documentation/ProcessDocumentationViewWrapper";
import { processURLPattern, processURLPatternBase } from "processEditor";

/* eslint-disable @typescript-eslint/no-explicit-any */

interface IProcessDataContainer {
    id?: any,
    data: IProcessData | null
}

const App: React.FC<React.PropsWithChildren<AppProps>> = (props) => {
    const [error, setError] = React.useState<Error | null>(null);
    const [hasUnsavedChanges, setHasUnsavedChanges] = React.useState<boolean>(false);
    const [theme] = useUserTheme();
    const chooseRestoreOrFetch = (container: IProcessDataContainer, fetchUri: string): IProcessData | string => {
        if (container.data) {
            if (confirm("This Process is either open in another tab or has an auto-saved version that can be restored.\n\nClick OK to restore, Cancel to discard.")) {
                // eslint-disable-next-line @typescript-eslint/no-non-null-assertion
                return container.data!;
            }

            // User did not want to restore -> remove the auto-saved Process
            removeFromLocalStorage(container.id);
        }

        return fetchUri;
    };

    const getProcessFetch = (action: string, isSubprocess: boolean, id?: any) => {
        const autoSavedData = { id: id, data: loadFromLocalStorage(id) };
        let editFetchUri = "";
        switch (action.toLowerCase()) {
            case UrlParamAction.new:
                return props.dispatch(
                    newProcess({ isSubprocess: isSubprocess }))
                    .processData.elementParameters;

            case UrlParamAction.newfromtemplate:
                return props.dispatch(processFromTemplate()).processData.elementParameters;

            case UrlParamAction.draft: // Edit draft
                return props.dispatch(
                    fetchProcess(
                        chooseRestoreOrFetch(autoSavedData, `api/process/draft/${id}`)) as any);

            case UrlParamAction.edit:
                if (validator.isNumeric(id)) { // Edit
                    editFetchUri = `api/process/?id=${id}`;
                } else if (validator.isUUID(id)) { // Fetch latest version
                    editFetchUri = `api/process/latestprocess/${id}`;
                }

                return props.dispatch(
                    fetchProcess(
                        chooseRestoreOrFetch(autoSavedData, editFetchUri)) as any);

            default:
                throw Error("Invalid uri");
        }
    }

    React.useEffect(() => {
        const handleFetchProcess = async () => {
            try {
                const lowerCaseActionName = props.match.params.action && props.match.params.action.toLowerCase();
                const isNewProcess = lowerCaseActionName === UrlParamAction.new || lowerCaseActionName === UrlParamAction.newfromtemplate;
                const [processElements] =
                    await Promise.all([
                        getProcessFetch(props.match.params.action, props.match.params.processType.toLowerCase() === ProcessType.Subprocess.toLowerCase(), props.match.params.id),
                        props.dispatch(fetchData(isNewProcess))
                    ]);

                props.dispatch(receiveAll(processElements as IElement[]) as any);
            } catch (errorMsg) {
                const error = errorMsg.exceptionMessage || errorMsg.message || errorMsg;
                Utilities.toastError(error);
                props.dispatch(receiveProcessError(error));
                setError(error);
            }
        }

        handleFetchProcess();
    }, []);

    React.useEffect(() => {
        const handleUnload = () => {
            if (hasUnsavedChanges) {
                removeFromLocalStorage(props.match.params.id as any);
            }
        };

        window.addEventListener("beforeunload", handleUnload);
        return () => {
            window.removeEventListener("beforeunload", handleUnload);
        };
    }, [hasUnsavedChanges, props.match.params.id]);

    useBeforeunload((handler) => {
        if (props.isDirty) {
            // Using a separate flag for the unload event. Trying to separate browser crashes from legimate use cases 
            setHasUnsavedChanges(true);
            handler.preventDefault();
            return FRENDS.Localizations.WarningUnsavedChanges;
        }
    });

    if (error) {
        return (
            <S.IndentedContainer>
                <h2>{FRENDS.Localizations.Error_Generic}</h2>
                <p>Please <u><a href="javascript:window.location.reload(true)">reload</a></u> the page.</p>
                <p className="small">{error.toString()}</p>
            </S.IndentedContainer>
        )
    }

    if (props.isLoading || !props.hasProcess) {
        return <PageLoadSpinner />;
    }

    const params = new URLSearchParams(props.location.search);
    const showDiff = params.get("diff") === "true";

    return (
        <ThemeProvider theme={{ mode: theme }}>
            <Switch>
                <Route path={`${processURLPattern}/documentation`}>
                    <ProcessDocumentationViewWrapper />
                </Route>
                <Route path={`${processURLPatternBase}/documentation`}>
                    <ProcessDocumentationViewWrapper />
                </Route>
                <Route>
                    <div>
                        {props.showRunOnceDialog ? <RunOnceDialogWrapper /> : null}
                        {props.showDependencyConstraintsConfirmation ? <DependencyConstraintsConfirmationModal /> : null}
                        {showDiff ? <DiffWorkingProcess /> : <ProcessEditorContainer />}
                    </div>
                </Route>
            </Switch>
        </ThemeProvider>
    )
}

export enum UrlParamAction {
    new = "new",
    newfromtemplate = "newfromtemplate",
    draft = "draft",
    edit = "edit"
}

interface AppStateProps {
    isDirty: boolean | undefined;
    hasProcess: boolean;
    isLoading: boolean | undefined;
    showRunOnceDialog: boolean | undefined;
    showDependencyConstraintsConfirmation: boolean | undefined;
}

interface AppDispatchProps {
    dispatch: ThunkDispatch<State, void, Redux.Action>;
}

type AppProps = RouteComponentProps<ParsedPathParts> & AppStateProps & AppDispatchProps;

const mapStateToProps = (state: State): AppStateProps => {
    return {
        isDirty: state.uiState.isDirty,
        isLoading: state.uiState.isLoading,
        hasProcess: state.process != null,
        showRunOnceDialog: state.uiState.showRunOnceDialog,
        showDependencyConstraintsConfirmation: state.uiState.dependencyConstraintsErrorMessage != null,
    };
};

const mapDispatchToProps = (dispatch: DispatchType<State>): AppDispatchProps => {
    return {
        dispatch: dispatch
    };
};

export default connect(mapStateToProps, mapDispatchToProps)(App);
